import { type FormEvent, useEffect, useRef, useState } from "react";

import { type ChatMessage, usePersonaChat } from "../hooks/usePersonaChat";

export interface PersonaChatProps {
  baseUrl?: string;
  stream?: boolean;
  /** Openers offered on the empty state. Keep them answerable from the profile. */
  suggestions?: string[];
}

const DEFAULT_SUGGESTIONS = [
  "What did you study?",
  "What have you built with Python?",
  "How does this chatbot work?",
  "What are you learning now?",
];

export function PersonaChat({
  baseUrl,
  stream = true,
  suggestions = DEFAULT_SUGGESTIONS,
}: PersonaChatProps) {
  const { messages, status, error, isBusy, send, retry, cancel, reset } = usePersonaChat({
    baseUrl,
    stream,
  });
  const [draft, setDraft] = useState("");
  const transcriptRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Follow the reply as it streams.
  useEffect(() => {
    const node = transcriptRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [messages]);

  function submit(event: FormEvent) {
    event.preventDefault();
    send(draft);
    setDraft("");
  }

  function ask(question: string) {
    send(question);
    inputRef.current?.focus();
  }

  const settled = status === "idle" ? messages.at(-1) : undefined;

  return (
    <section
      className="rounded-app border border-border bg-surface shadow-sm"
      aria-label="Chat with Irfan's AI persona"
    >
      <header className="flex items-center gap-3 border-b border-border px-5 py-4">
        <span
          className="grid size-9 shrink-0 place-items-center rounded-full bg-primary text-sm font-semibold text-primary-fg"
          aria-hidden="true"
        >
          I
        </span>
        <span className="flex-1">
          <span className="block text-sm font-semibold text-title">Irfan</span>
          <span className="block text-xs text-text-light">
            {isBusy ? "Typing…" : "AI persona · answers from what I've written"}
          </span>
        </span>
        {messages.length > 0 && (
          <button
            type="button"
            onClick={() => void reset()}
            className="rounded-app px-2 py-1 text-xs text-text-light transition-colors hover:bg-muted hover:text-title"
          >
            Clear
          </button>
        )}
      </header>

      <div
        ref={transcriptRef}
        className="flex max-h-[26rem] min-h-[16rem] flex-col gap-5 overflow-y-auto px-5 py-6"
      >
        {messages.length === 0 ? (
          <EmptyState suggestions={suggestions} onAsk={ask} />
        ) : (
          messages.map((message) => <Message key={message.id} message={message} />)
        )}
      </div>

      {/* Announced once the reply settles, rather than on every token. */}
      <p className="sr-only-live" aria-live="polite" aria-atomic="true">
        {settled?.role === "assistant" ? settled.content : ""}
      </p>

      {error && (
        <div
          role="alert"
          className="mx-5 mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-app border border-red-500/40 bg-red-500/5 px-4 py-3 text-sm text-red-500 dark:text-red-400"
        >
          <span className="flex-1">{error.message}</span>
          {error.isRetryable && (
            <button
              type="button"
              onClick={retry}
              className="font-medium underline underline-offset-2"
            >
              {error.code === "rate_limited" && error.retryAfterSeconds
                ? `Try again in ${error.retryAfterSeconds}s`
                : "Try again"}
            </button>
          )}
        </div>
      )}

      <form onSubmit={submit} className="flex gap-2 border-t border-border px-5 py-4">
        <label htmlFor="persona-input" className="sr-only-live">
          Your message
        </label>
        <input
          id="persona-input"
          ref={inputRef}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="Ask about my work…"
          autoComplete="off"
          disabled={isBusy}
          className="min-w-0 flex-1 rounded-app border border-border bg-bg px-4 py-2.5 text-sm text-title placeholder:text-text-light disabled:opacity-60"
        />
        {isBusy ? (
          <button
            type="button"
            onClick={cancel}
            className="rounded-app border border-border px-4 py-2.5 text-sm font-medium text-text transition-colors hover:bg-muted"
          >
            Stop
          </button>
        ) : (
          <button
            type="submit"
            disabled={!draft.trim()}
            className="inline-flex items-center gap-2 rounded-app bg-primary px-4 py-2.5 text-sm font-medium text-primary-fg transition-colors hover:bg-primary-alt disabled:opacity-40"
          >
            Send
            <SendIcon />
          </button>
        )}
      </form>
    </section>
  );
}

function EmptyState({
  suggestions,
  onAsk,
}: {
  suggestions: string[];
  onAsk: (question: string) => void;
}) {
  return (
    <div className="m-auto max-w-md text-center">
      <p className="text-sm text-text">
        This is an AI version of me. Pick a question, or ask your own.
      </p>
      <ul className="mt-4 flex flex-wrap justify-center gap-2">
        {suggestions.map((question) => (
          <li key={question}>
            <button
              type="button"
              onClick={() => onAsk(question)}
              className="rounded-app border border-primary/45 px-3 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary hover:text-primary-fg"
            >
              {question}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Message({ message }: { message: ChatMessage }) {
  if (message.role === "user") {
    return (
      <p className="ml-auto max-w-[85%] rounded-app bg-primary px-4 py-2.5 text-sm text-primary-fg">
        {message.content}
      </p>
    );
  }

  // A turn that failed before a single token arrived has nothing to show. The
  // error banner below already explains what happened, so rendering an empty
  // box with a red hairline would just be duplicate noise.
  if (message.failed && !message.content) return null;

  const pending = !message.content && !message.failed;

  return (
    <div
      className={`relative max-w-[92%] py-0.5 pl-4 ${message.failed ? "opacity-55" : ""}`}
      data-failed={message.failed || undefined}
    >
      {/* The generation rail — see .rail in index.css. */}
      <span
        className={`rail ${message.streaming || pending ? "rail--live" : ""} ${
          message.failed ? "!bg-red-500/60" : ""
        }`}
        aria-hidden="true"
      />
      <p className="text-sm leading-relaxed whitespace-pre-wrap text-text">
        {/* Non-streaming mode buffers the whole reply, so the transcript needs
            something anchored here while the request is in flight. */}
        {pending ? <span className="text-text-light">Thinking…</span> : message.content}
        {message.streaming && <span className="caret" aria-hidden="true" />}
      </p>
    </div>
  );
}

function SendIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 12h15M13 6l6 6-6 6" />
    </svg>
  );
}
